import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import useCarousel from "../hooks/useCarousel";
import useCarouselSlideIndex from "../hooks/useCarouselSlideIndex";
import type { DivProps } from "components/primitives/Div";

/** Outranks the consumer's `sx` or class, so a `display` of their own cannot show an inactive slide. */
const CarouselSlideRoot = styled("div")({
  "&[hidden]": {
    display: "none",
  },
});

/** `style` is reserved: inline styles would beat the hidden rule. Style slides through `sx`. */
export interface CarouselSlideProps extends Omit<DivProps, "style"> {}

/**
 * @description A single slide. Only the active one is shown, and each one is announced by its position,
 * e.g. "1 of 2". Must be a direct child of `<CarouselSlides />`.
 */
function CarouselSlide(props: CarouselSlideProps) {
  const { t } = useTranslation();
  const { activeStep, steps } = useCarousel();
  const index = useCarouselSlideIndex();

  return (
    <CarouselSlideRoot
      role="group"
      aria-roledescription={t("global.slide_role_description")}
      aria-label={t("global.carousel_slide_label", { step: index + 1, steps })}
      hidden={index !== activeStep}
      {...props}
    />
  );
}

export default CarouselSlide;
